import React, { useCallback, useMemo } from 'react';
import './BuildStats.css';
import * as st from '../../core/st';
import Build from '../../core/Build';
import StatWidget from './StatWidget';
import StatHeaderWidget from './StatHeaderWidget';
import { statWeightKey } from '../../core/criteriaWeights';

const BuildStats = ({
    build,
    setBuild,
    lang,
    genConfig,
    setGenConfig,
    isGenerating = false,
    mobileTargetCheckbox = false,
    canEditBuild = true,
    canEditGenerator = true,
}) => {
    const useCompactLayout = !canEditGenerator || mobileTargetCheckbox;
    // The weights column rides with the full generator rows only. Compact rows
    // (mobile, read-only) don't edit criteria at all — their targets are edited
    // from the gen bar, and so are their weights.
    const showWeights = canEditGenerator && !useCompactLayout && genConfig?.displayWeights === true;
    const isCompactMobile = useCompactLayout && mobileTargetCheckbox;
    const isCompactDesktop = useCompactLayout && !mobileTargetCheckbox;

    const includePassiveEffects = genConfig?.displayPassiveBonuses === true;
    const passiveOverrides = useMemo(() => genConfig?.passiveOverrides || {}, [genConfig?.passiveOverrides]);
    const stats = useMemo(
        () => build ? build.getStats({ includePassiveEffects, passiveOverrides }) : {},
        [build, includePassiveEffects, passiveOverrides]
    );
    const characterPointsValue = build?.getRemainingCharacterPoints() ?? 0;

    const onBaseValueChange = useCallback((statId, inputBaseValue) => {
        if (!build || !canEditBuild) return;
        setBuild(build.setBaseAttribute(statId, inputBaseValue));
    }, [build, canEditBuild, setBuild]);

    const onScrollValueChange = useCallback((statId, inputScrollValue) => {
        if (!build || !canEditBuild) return;
        setBuild(build.setScroll(statId, inputScrollValue));
    }, [build, canEditBuild, setBuild]);

    const onDesiredValueChange = useCallback((statId, inputDesiredValue) => {
        if (!build || !canEditGenerator) return;
        setBuild(build.setDesired(statId, inputDesiredValue));
    }, [build, canEditGenerator, setBuild]);

    const onMaximizeStatChange = useCallback(
        (statId, enabled) => {
            if (!build || !canEditGenerator) return;
            setBuild(build.setMaximizeStat(statId, enabled));
        },
        [build, canEditGenerator, setBuild]
    );

    const onWeightChange = useCallback((statId, weight) => {
        if (!build || !canEditGenerator) return;
        setBuild(build.setCriterionWeight(statWeightKey(statId), weight));
    }, [build, canEditGenerator, setBuild]);

    const onExitMaximizeToValue = useCallback(
        (statId, seedValue) => {
            if (!build || !canEditGenerator) return;
            let b = build.setMaximizeStat(statId, false);
            b = b.setDesired(statId, seedValue);
            setBuild(b);
        },
        [build, canEditGenerator, setBuild]
    );

    const hasActiveElementalTargets = useMemo(() => {
        const elementalStatIds = [st.STRENGTH_ID, st.INTELLIGENCE_ID, st.CHANCE_ID, st.AGILITY_ID];
        return elementalStatIds.some(statId => {
            const statData = stats[statId];
            if (!statData) return false;
            if (statData.maximize) return true;
            return statData.desired !== null && statData.desired !== undefined;
        });
    }, [stats]);

    const hasActiveElementalDamageTargets = useMemo(() => {
        const elementalDamageStatIds = [92, 88, 89, 90, 91];
        return elementalDamageStatIds.some(statId => {
            const statData = stats[statId];
            if (!statData) return false;
            if (statData.maximize) return true;
            return statData.desired !== null && statData.desired !== undefined;
        });
    }, [stats]);

    const renderStatWidgets = useCallback((statList) => {
        const powerStat = stats[st.POWER_ID];
        const powerValue = powerStat?.current || 0;
        const damageStat = stats[st.DAMAGE_ID];
        const damageValue = damageStat?.current || 0;
        const critDamageStat = stats[st.CRIT_DAMAGE_ID];
        const critDamageValue = critDamageStat?.current || 0;
        return statList.map((stat, index) => {
            // null = layout gap (WYSIWYG compact config): keep the row, render blank.
            if (!stat) {
                return <div className="stat-widget-spacer" key={`gap-${index}`} aria-hidden="true" />;
            }
            const hasSpecialParams = !["damage", "damage_other", "resistance", "resistance_other", "resistance_fixed"].includes(stat.category);
            return (
                <StatWidget
                    key={stat.id}
                    // The level-aware minimum (AP is 7 from level 100): it is where
                    // the slider's first target notch sits, and a target there is a floor.
                    stat={{ ...stat, ...stats[stat.id], minRange: Build.getEffectiveMinRange(stat, build?.lvl) }}
                    mobileTargetCheckbox={mobileTargetCheckbox}
                    compactRow={useCompactLayout}
                    isGenerating={isGenerating}
                    canEditBuild={canEditBuild}
                    canEditGenerator={canEditGenerator}
                    powerValue={powerValue}
                    damageValue={damageValue}
                    critDamageValue={critDamageValue}
                    hasActiveElementalTargets={hasActiveElementalTargets}
                    hasActiveElementalDamageTargets={hasActiveElementalDamageTargets}
                    onScrollValueChange={onScrollValueChange}
                    onBaseValueChange={onBaseValueChange}
                    onDesiredValueChange={onDesiredValueChange}
                    onMaximizeStatChange={onMaximizeStatChange}
                    onExitMaximizeToValue={onExitMaximizeToValue}
                    characterPoints={characterPointsValue}
                    showWeight={showWeights}
                    weight={showWeights && build ? build.getCriterionWeight(statWeightKey(stat.id)) : 1}
                    onWeightChange={onWeightChange}
                    lang={lang}
                    genConfig={genConfig}
                    setGenConfig={setGenConfig}
                    hasSpecialParams={hasSpecialParams}
                />
            );
        });
    }, [stats, hasActiveElementalTargets, hasActiveElementalDamageTargets, onBaseValueChange, onScrollValueChange, onDesiredValueChange, onMaximizeStatChange, onExitMaximizeToValue, characterPointsValue, lang, genConfig, setGenConfig, isGenerating, mobileTargetCheckbox, useCompactLayout, canEditBuild, canEditGenerator, build, showWeights, onWeightChange]);

    const categoryGroups = useMemo(
        () => {
            const layout = isCompactMobile
                ? st.STAT_COMPACT_MOBILE_LAYOUT
                : st.STAT_COMPACT_DESKTOP_LAYOUT;
            const groups = st.orderCompactPanelGroups(st.resolveCompactPanelGroups(layout), {
                useDesktopColumnHints: isCompactDesktop,
            });
            if (!canEditGenerator) return groups;
            return groups.map((group) => ({
                ...group,
                columns: group.columns.map((col) =>
                    col.map((stat) => (stat && st.isStatHiddenInGenerator(stat.id) ? null : stat))
                ),
            }));
        },
        [canEditGenerator, isCompactMobile, isCompactDesktop]
    );

    // Desktop compact: bucket into @0 / @1; CSS picks side-by-side vs stacked from panel width.
    const compactDesktopColumns = useMemo(
        () => (isCompactDesktop
            ? st.groupCompactPanelByDesktopColumn(categoryGroups)
            : null),
        [categoryGroups, isCompactDesktop],
    );

    const showCompactCategoryHeaders = isCompactMobile
        ? st.STAT_COMPACT_SHOW_CATEGORY_HEADERS?.mobile !== false
        : st.STAT_COMPACT_SHOW_CATEGORY_HEADERS?.desktop !== false;

    const useMobilePanelLayout = st.STAT_PANEL_LAYOUT_VARIANT === 'mobile';

    const desktopColumns = useMemo(() => {
        const cfg = useMobilePanelLayout
            ? st.STAT_PANEL_CONFIG
            : st.STAT_PANEL_CONFIG_LEGACY;
        const normalizeGroup = group => (typeof group === 'string' ? { category: group } : group);
        const hasStats = group => (
            Array.isArray(group.stats)
                ? group.stats.some(name => st.STATS.some(stat => stat.name === name))
                : st.STATS.some(stat => stat.category === group.category)
        );
        return [
            { key: "left", groups: (cfg.left || []).map(normalizeGroup) },
            { key: "right", groups: (cfg.right || []).map(normalizeGroup) },
        ].map(column => ({
            ...column,
            groups: column.groups.filter(hasStats),
        })).filter(column => column.groups.length > 0);
    }, [useMobilePanelLayout]);

    const mobilePanelRows = useMemo(() => {
        if (!useMobilePanelLayout) return [];
        const leftGroups = desktopColumns.find(column => column.key === 'left')?.groups || [];
        const rightGroups = desktopColumns.find(column => column.key === 'right')?.groups || [];
        const rowCount = Math.max(leftGroups.length, rightGroups.length);
        return Array.from({ length: rowCount }, (_, index) => ({
            left: leftGroups[index] || null,
            right: rightGroups[index] || null,
        }));
    }, [desktopColumns, useMobilePanelLayout]);

    // Every compact header renders the same way: as the first row inside its
    // column's own stat grid (not a separate sibling grid), so headers share
    // identical spacing/height everywhere and the attribute-points badge lines
    // up pixel-for-pixel with the stat rows below it. A null category renders
    // a blank placeholder so headerless columns keep their stat rows aligned.
    const renderCompactColumnHeader = useCallback((columnCategory, columnIndex) => {
        const columnName = columnCategory ? st.getCompactStatCategory(columnCategory, lang) : '';
        const isAttributeColumn = columnCategory === 'attribute';

        return (
            <React.Fragment key={`header-${columnIndex}`}>
                <h6
                    className={[
                        'stat-header',
                        'stat-compact-category-title',
                        isAttributeColumn ? 'stat-compact-category-title--attribute' : '',
                        columnCategory === 'entrave' ? 'stat-compact-category-title--control' : '',
                    ].filter(Boolean).join(' ')}
                    aria-hidden={columnName ? undefined : true}
                >
                    <span className="stat-compact-category-label">{columnName || ' '}</span>
                </h6>
                {isAttributeColumn && (
                    <span className="stat-compact-attribute-points">{characterPointsValue}</span>
                )}
            </React.Fragment>
        );
    }, [characterPointsValue, lang]);

    const renderCompactCategory = (group, groupIndex) => {
        // Per-column headers when the layout defines them; otherwise the single
        // title goes in the first column and the rest get blank placeholders.
        // null = headerless section (no header row at all).
        const singleTitle = st.getCompactStatCategory(group.category, lang);
        const columnHeaders = showCompactCategoryHeaders
            ? ((Array.isArray(group.columnCategories) && group.columnCategories.length > 0)
                ? group.columnCategories
                : (singleTitle ? group.columns.map((_, index) => (index === 0 ? group.category : null)) : null))
            : null;

        return (
            <section
                className="stat-category-group"
                data-compact-category={group.category}
                style={{ '--compact-group-cols': group.columns.length }}
                key={`${group.category}-${groupIndex}`}
            >
                {group.columns.map((statList, columnIndex) => (
                    <div
                        className={`stat-panel stat-category stat-panel-column-${columnIndex + 1}`}
                        key={`${group.category}-${columnIndex}`}
                    >
                        {columnHeaders && renderCompactColumnHeader(columnHeaders[columnIndex] ?? null, columnIndex)}
                        {renderStatWidgets(statList)}
                    </div>
                ))}
            </section>
        );
    };

    const getDesktopGroupStats = useCallback((group) => {
        const statList = Array.isArray(group.stats)
            ? group.stats.map(name => st.STATS.find(stat => stat.name === name)).filter(Boolean)
            : st.STATS.filter(stat => stat.category === group.category);
        return statList.filter(stat => !(canEditGenerator && st.isStatHiddenInGenerator(stat.id)));
    }, [canEditGenerator]);

    const renderDesktopGroup = (group, index, { showHeader = true } = {}) => {
        const categoryName = useMobilePanelLayout
            ? st.getCompactStatCategory(group.category, lang)
            : st.getStatCategory(group.category, lang);
        return (
            <React.Fragment key={`${group.category}-${index}`}>
                {showHeader && (
                    <StatHeaderWidget
                        isPrimary={group.category === "primary"}
                        isAttribute={group.category === "attribute"}
                        isTopHeader={index === 0}
                        categoryName={categoryName}
                        characterPoints={group.category === "attribute" ? characterPointsValue : undefined}
                        simpleMode={!canEditGenerator}
                    />
                )}
                {renderStatWidgets(getDesktopGroupStats(group))}
            </React.Fragment>
        );
    };

    const renderMobilePanelRows = () => (
        mobilePanelRows.map((row, index) => (
            <React.Fragment key={`mobile-panel-row-${index}`}>
                {index > 0 && (
                    <div className="stat-panel-block-divider" aria-hidden="true" />
                )}
                <div className="stat-panel stat-category stat-panel-left column">
                    {row.left && renderDesktopGroup(row.left, index, { showHeader: false })}
                </div>
                <div className="stat-panel stat-category stat-panel-right column">
                    {row.right && renderDesktopGroup(row.right, index, { showHeader: false })}
                </div>
            </React.Fragment>
        ))
    );

    const renderDesktopContent = () => {
        if (useMobilePanelLayout) return renderMobilePanelRows();
        return desktopColumns.map(column => (
            <div
                className={`stat-panel stat-category stat-panel-${column.key} column`}
                key={column.key}
            >
                {column.groups.map(renderDesktopGroup)}
            </div>
        ));
    };

    const rootClassName = [
        'build-stats',
        'column',
        useCompactLayout ? 'build-stats--compact' : '',
        useMobilePanelLayout && !useCompactLayout ? 'build-stats--mobile-panel' : '',
        isCompactMobile ? 'build-stats--compact-mobile' : '',
        isCompactDesktop ? 'build-stats--compact-desktop' : '',
        useCompactLayout && !showCompactCategoryHeaders ? 'build-stats--compact-no-headers' : '',
        !canEditGenerator ? 'build-stats--simple' : '',
        showWeights ? 'build-stats--weights' : '',
    ].filter(Boolean).join(' ');

    const contentClassName = useCompactLayout
        ? 'build-stats-content build-stats-content--compact'
        : 'build-stats-content row';

    const renderCompactContent = () => {
        if (compactDesktopColumns) {
            return (
                <div className="build-stats-compact-columns">
                    {compactDesktopColumns.map((columnGroups, columnIndex) => (
                        <div className="build-stats-compact-column" key={`compact-col-${columnIndex}`}>
                            {columnGroups.map(renderCompactCategory)}
                        </div>
                    ))}
                </div>
            );
        }
        return categoryGroups.map(renderCompactCategory);
    };

    return (
        <div className={rootClassName}>
            <div className={contentClassName}>
                {useCompactLayout
                    ? renderCompactContent()
                    : renderDesktopContent()}
            </div>
        </div>
    );
};

export default BuildStats;
